import React, { PropsWithChildren, ReactNode } from 'react'
import clsx from 'clsx'
import { Input as InputStyles, InputAdornment as InputAdornmentStyles } from '@airbus/elements/elements'
import { createComponent, createUseStyles } from '../../lib/styling'
import { CommonProps } from '../helperTypes'

const useStyles = createUseStyles(InputStyles, 'input')

type Props = {
  /** end adornment node */
  endAdornment?: ReactNode
  /** optional react ref to pass to native input */
  inputRef?: React.RefObject<HTMLInputElement>
  /** If true, the input will indicate an error */
  invalid?: boolean
  /** input change callback */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, value: any) => void
  /** key down event handler */
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>
  /** key up event handler */
  onKeyUp?: React.KeyboardEventHandler<HTMLInputElement>
  /**
   * The size of the input
   * @default medium
   */
  size?: 'small' | 'medium' | 'large'
  /** start adornment node */
  startAdornment?: ReactNode
  /** If true, the input will indicate a success */
  valid?: boolean
}

export type InputProps = Props & CommonProps

const Input = ({ size = 'medium', startAdornment, endAdornment, inputRef, onChange, ...rest }: InputProps) => {
  const handleChange = React.useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => onChange && onChange(evt, evt.target.value),
    [onChange],
  )

  const adornedStart = startAdornment !== undefined
  const adornedEnd = endAdornment !== undefined
  const adorned = adornedStart || adornedEnd
  if (adorned) {
    return (
      <InputGroup>
        {adornedStart && (
          <InputAdornment position='start' size={size}>
            {startAdornment}
          </InputAdornment>
        )}
        <InputBase ref={inputRef} onChange={handleChange} {...{ size, adornedStart, adornedEnd }} {...rest} />
        {adornedEnd && (
          <InputAdornment position='end' size={size}>
            {endAdornment}
          </InputAdornment>
        )}
      </InputGroup>
    )
  }
  return <InputBase ref={inputRef} size={size} onChange={handleChange} {...rest} />
}
export default Input

export const InputBase = createComponent<HTMLInputElement, InputProps>(
  'input',
  InputStyles,
  ({ className, size, invalid, valid, adornedStart, adornedEnd, ...rest }, classes) => ({
    ...rest,
    ...(invalid && { 'aria-invalid': true }),
    className: clsx(
      className,
      classes.root,
      size && classes[`_${size}`],
      valid && classes._valid,
      invalid && classes._invalid,
      adornedStart && classes._adornedStart,
      adornedEnd && classes._adornedEnd,
    ),
  }),
)

export type InputAdornmentProps = {
  /** The side of the input where the adornment is integrated */
  position: 'start' | 'end'
  /** The size of the adornment's input */
  size?: 'small' | 'medium' | 'large'
}

export const InputAdornment = createComponent<HTMLSpanElement, InputAdornmentProps>(
  'span',
  InputAdornmentStyles,
  ({ className, position, size, ...rest }, classes) => ({
    className: clsx(className, classes.root, position && classes[`_${position}`], size && classes[`_${size}`]),
    ...rest,
  }),
)

export const InputGroup = ({ children, className, ...rest }: PropsWithChildren<CommonProps>) => {
  const classes = useStyles()
  return (
    <div className={clsx(className, classes.group)} {...rest}>
      {children}
    </div>
  )
}
